import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBusinessContext } from '../contexts/BusinessContext';
import IndustrySelector from '../components/IndustrySelector';
import api from '../services/api';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6Lf4BB8sAAAAANqfyTNMUA_l7DTm8LZQIl03QOAu';

const SITE_IS_LIVE = true; // ← Match this to your App.jsx setting

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useBusinessContext();
  
  // Get beta code from URL
  const betaCode = searchParams.get('beta');

  // Beta validation state
  const [betaValidation, setBetaValidation] = useState({
    isChecking: true,
    isValid: null,
    error: null,
    lockedToEmail: null
  });

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    businessName: '',
    businessPhone: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    // Industry selection
    primaryServiceType: '',
    additionalServiceTypes: [],
    // SMS opt-in
    smsOptIn: false,
    // Honeypot fields - hidden from user
    website: '',
    companyUrl: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // US States and Territories
  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    // Territories
    'AS', 'GU', 'MP', 'PR', 'VI'
  ];

  // Validate beta code on mount (or skip if site is live)
  useEffect(() => {
    const validateBetaCode = async () => {
      // If site is live and no beta code, allow normal registration
      if (SITE_IS_LIVE && !betaCode) {
        setBetaValidation({
          isChecking: false,
          isValid: true,
          error: null,
          lockedToEmail: null
        });
        return;
      }

      // No beta code when site is NOT live = redirect to coming soon
      if (!betaCode) {
        navigate('/');
        return;
      }

      // Has beta code - validate it
      try {
        const response = await api.post('/beta/validate', { code: betaCode });
        const result = response.data;

        if (result.isValid) {
          setBetaValidation({
            isChecking: false,
            isValid: true,
            error: null,
            lockedToEmail: result.lockedToEmail
          });

          // If code is locked to an email, pre-fill it
          if (result.lockedToEmail) {
            setFormData(prev => ({ ...prev, email: result.lockedToEmail }));
          }
        } else {
          setBetaValidation({
            isChecking: false,
            isValid: false,
            error: result.reason,
            lockedToEmail: null
          });
        }
      } catch (err) {
        console.error('Beta code validation error:', err);
        setBetaValidation({
          isChecking: false,
          isValid: false,
          error: 'Unable to validate access code. Please try again.',
          lockedToEmail: null
        });
      }
    };

    validateBetaCode();
  }, [betaCode, navigate]);

  // Load reCAPTCHA v3 script (only if beta is valid)
  useEffect(() => {
    // Don't load reCAPTCHA until beta is validated
    if (betaValidation.isChecking || !betaValidation.isValid) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
      console.log('reCAPTCHA v3 loaded successfully');
    };
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA v3');
      setErrors({ submit: 'Failed to load security verification. Please refresh the page.' });
    };
    document.body.appendChild(script);

    return () => {
      // Only remove if it exists
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [betaValidation.isChecking, betaValidation.isValid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handlePrimaryServiceChange = (serviceType) => {
    setFormData(prev => ({ ...prev, primaryServiceType: serviceType }));
    if (errors.primaryServiceType) {
      setErrors(prev => ({ ...prev, primaryServiceType: '' }));
    }
  };

  const handleAdditionalServicesChange = (services) => {
    setFormData(prev => ({ ...prev, additionalServiceTypes: services }));
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Personal information
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    
    // Phone validation (optional but must be valid format if provided)
    if (formData.phoneNumber) {
      const digits = formData.phoneNumber.replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
      }
    }

    // Business phone validation (optional but must be valid format if provided)
    if (formData.businessPhone) {
      const digits = formData.businessPhone.replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        newErrors.businessPhone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    // Business information
    if (!formData.businessName) newErrors.businessName = 'Business name is required';
    
    // Industry selection
    if (!formData.primaryServiceType) {
      newErrors.primaryServiceType = 'Please select your primary service type';
    }
    
    return newErrors;
  };

  const getRecaptchaToken = async () => {
    if (!window.grecaptcha || !recaptchaLoaded) {
      console.error('reCAPTCHA not loaded');
      throw new Error('Security verification not ready. Please refresh the page.');
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { 
        action: 'registration' 
      });
      return token;
    } catch (error) {
      console.error('Error getting reCAPTCHA token:', error);
      throw new Error('Security verification failed. Please try again.');
    }
  };

  // Parse ASP.NET Core validation errors into field-specific errors
  const parseApiErrors = (errorResponse) => {
    const fieldErrors = {};
    
    // Check for ASP.NET Core validation error format
    if (errorResponse?.errors) {
      // Map API field names to form field names
      const fieldMapping = {
        'Phone': 'phoneNumber',
        'Email': 'email',
        'Password': 'password',
        'FirstName': 'firstName',
        'LastName': 'lastName',
        'BusinessName': 'businessName',
        'BusinessPhone': 'businessPhone',
        'BusinessAddress': 'businessAddress',
        'City': 'city',
        'State': 'state',
        'ZipCode': 'zipCode'
      };

      // Friendly error messages
      const friendlyMessages = {
        'Invalid phone number': 'Please enter a valid phone number (e.g., (555) 123-4567)',
        'The Phone field is not a valid phone number.': 'Please enter a valid phone number (e.g., (555) 123-4567)',
      };

      Object.entries(errorResponse.errors).forEach(([field, messages]) => {
        const formField = fieldMapping[field] || field.toLowerCase();
        const message = Array.isArray(messages) ? messages[0] : messages;
        fieldErrors[formField] = friendlyMessages[message] || message;
      });
    }
    
    return fieldErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstError = document.querySelector('.text-red-600');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();

      // Call register with ALL fields including industry selection and beta code
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phoneNumber, // Changed from phoneNumber to phone for API
        businessName: formData.businessName,
        businessPhone: formData.businessPhone,
        businessAddress: formData.businessAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        // Industry selection
        primaryServiceType: formData.primaryServiceType,
        additionalServiceTypes: formData.additionalServiceTypes,
        // SMS opt-in
        smsOptIn: formData.smsOptIn,
        // Spam prevention fields
        recaptchaToken: recaptchaToken,
        website: formData.website,      // Honeypot (should be empty)
        companyUrl: formData.companyUrl, // Honeypot (should be empty)
        trialDays: 14, // Default 14-day trial
        // Include beta code
        betaCode: betaCode
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error types
      if (error.response?.data?.errors) {
        // ASP.NET Core validation errors - parse and show inline
        const fieldErrors = parseApiErrors(error.response.data);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          // Scroll to first error
          setTimeout(() => {
            const firstError = document.querySelector('.text-red-600');
            if (firstError) {
              firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          setErrors({ submit: 'Please check your information and try again.' });
        }
      } else if (error.response?.data?.reasons && Array.isArray(error.response.data.reasons)) {
        // Spam prevention blocked
        setErrors({ 
          submit: error.response.data.reasons.join('. ') 
        });
      } else if (error.response?.data?.error) {
        // General API error
        setErrors({ 
          submit: error.response.data.error 
        });
      } else if (error.message) {
        // Client-side error (like reCAPTCHA failure)
        setErrors({ 
          submit: error.message 
        });
      } else {
        // Unknown error
        setErrors({ 
          submit: 'Registration failed. Please try again.' 
        });
      }
      
      // Scroll to error message if it's a submit error
      if (!error.response?.data?.errors) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking beta code
  if (betaValidation.isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-blue-200">Validating access code...</p>
        </div>
      </div>
    );
  }

  // Show error if beta code is invalid
  if (!betaValidation.isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Access Code</h2>
          <p className="text-gray-600 mb-6">{betaValidation.error}</p>
          <Link
            to="/"
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium py-2 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src="/images/SuddyFlow-Logo-PNG.png" 
              alt="SuddyFlow" 
              className="h-10 mx-auto mb-6"
            />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              Start Your Free Trial
            </h1>
            <p className="text-gray-600 mt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                Sign in
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {errors.submit}
              </div>
            )}

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`input ${errors.firstName ? 'border-red-300' : ''}`}
                    disabled={loading}
                  />
                  {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="label">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`input ${errors.lastName ? 'border-red-300' : ''}`}
                    disabled={loading}
                  />
                  {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="label">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input ${errors.email ? 'border-red-300' : ''} ${betaValidation.lockedToEmail ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={loading || !!betaValidation.lockedToEmail}
                  />
                  {betaValidation.lockedToEmail && (
                    <p className="mt-1 text-xs text-gray-500">This invite is reserved for this email address</p>
                  )}
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label className="label">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`input ${errors.password ? 'border-red-300' : ''}`}
                    disabled={loading}
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>

                <div>
                  <label className="label">Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`input ${errors.confirmPassword ? 'border-red-300' : ''}`}
                    disabled={loading}
                  />
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="label">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    className={`input ${errors.phoneNumber ? 'border-red-300' : ''}`}
                    placeholder="(555) 123-4567"
                    disabled={loading}
                  />
                  {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>}
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="label">Business Name *</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    className={`input ${errors.businessName ? 'border-red-300' : ''}`}
                    disabled={loading}
                  />
                  {errors.businessName && <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="label">Business Phone</label>
                  <input
                    type="tel"
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={handlePhoneChange}
                    className={`input ${errors.businessPhone ? 'border-red-300' : ''}`}
                    placeholder="(555) 123-4567"
                    disabled={loading}
                  />
                  {errors.businessPhone && <p className="mt-1 text-sm text-red-600">{errors.businessPhone}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="label">Street Address</label>
                  <input
                    type="text"
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleChange}
                    className="input"
                    placeholder="123 Main Street"
                    disabled={loading}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input"
                    placeholder="Mooresville"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="label">States & Territories</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="input"
                    disabled={loading}
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="input"
                    placeholder="28117"
                    maxLength="10"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Industry Selection */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What Services Do You Offer?</h3>
              <p className="text-sm text-gray-600 mb-6">
                Select your primary service to customize your experience. We'll pre-configure relevant job templates for you!
              </p>
              
              <IndustrySelector
                value={formData.primaryServiceType}
                onChange={handlePrimaryServiceChange}
                additionalServices={formData.additionalServiceTypes}
                onAdditionalChange={handleAdditionalServicesChange}
                showPreview={true}
              />
              
              {errors.primaryServiceType && (
                <p className="mt-2 text-sm text-red-600">{errors.primaryServiceType}</p>
              )}
            </div>

            {/* SMS Opt-In */}
            <div className="pt-6 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="smsOptIn"
                  checked={formData.smsOptIn}
                  onChange={handleCheckboxChange}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="text-sm text-gray-600">
                  I agree to receive SMS notifications about new leads, appointments, and important account alerts. 
                  Message & data rates may apply. Reply STOP to opt out at any time.{' '}
                  <Link to="/sms-policy" className="text-blue-600 hover:text-blue-700 underline">
                    SMS Policy
                  </Link>
                </span>
              </label>
            </div>

            {/* Honeypot Fields - Hidden from users but visible to bots */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
              <input
                type="text"
                name="companyUrl"
                tabIndex="-1"
                autoComplete="off"
                value={formData.companyUrl}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !recaptchaLoaded}
              className="w-full px-4 py-4 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white font-semibold text-lg rounded-xl hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Your Account...
                </span>
              ) : (
                '🚀 Create Account & Start Free Trial'
              )}
            </button>
            
            <div className="text-xs text-center text-gray-500 mt-4">
              By registering, you agree to our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-700">
                Terms of Service
              </Link>.
              {' '}This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" className="text-blue-600 hover:text-blue-700" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" className="text-blue-600 hover:text-blue-700" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white text-sm flex items-center justify-center gap-2 flex-wrap">
            <span className="bg-white/20 px-3 py-1 rounded-full font-semibold text-xs backdrop-blur-sm">🇺🇸 VETERAN-OWNED</span>
            <span className="hidden sm:inline">•</span>
            <span>14-Day Free Trial</span>
            <span className="hidden sm:inline">•</span>
            <span>No Credit Card Required</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;